import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  Button,
  message,
  Select,
  Divider,
  Tooltip,
  Upload,
} from "antd";
import {
  CarOutlined,
  SaveOutlined,
  CloseOutlined,
  PlusOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { createVehicle } from "../../api/vehicle/vehicleRequest";
import {
  colorMap,
  commonBrands,
  commonColors,
} from "../../utils/themeConstant";
import { getMessageError } from "../../utils/messageUtils";
import { countries } from "../../utils/countries";
import { handleFilename } from "../../utils";

const { Option } = Select;

const VehicleCreateModal = ({ visible, onCancel, onCreateVehicle }) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [brands, setBrands] = useState([]);
  const [colors, setColors] = useState([]);
  const [newBrand, setNewBrand] = useState("");
  const [newColor, setNewColor] = useState("");
  const [colorHex, setColorHex] = useState("#CCCCCC");
  const [showAddBrand, setShowAddBrand] = useState(false);
  const [showAddColor, setShowAddColor] = useState(false);
  const [fileList, setFileList] = useState([]);

  useEffect(() => {
    setBrands([...commonBrands]);
    setColors([...commonColors]);
  }, []);

  useEffect(() => {
    if (visible) {
      form.resetFields();
    }
  }, [visible, form]);

  const handleAddBrand = () => {
    if (newBrand && newBrand.trim() !== "") {
      if (brands.includes(newBrand.trim())) {
        message.warning("Cette marque existe déjà !");
        return;
      }

      const updatedBrands = [...brands, newBrand.trim()];
      setBrands(updatedBrands);
      form.setFieldsValue({ brand: newBrand.trim() });
      setNewBrand("");
      setShowAddBrand(false);
      message.success("Marque ajoutée avec succès");
    } else {
      message.error("Veuillez saisir un nom de marque valide");
    }
  };

  const handleAddColor = () => {
    if (newColor && newColor.trim() !== "") {
      if (colors.includes(newColor.trim())) {
        message.warning("Cette couleur existe déjà !");
        return;
      }

      const updatedColorMap = { ...colorMap };
      updatedColorMap[newColor.trim()] = colorHex;

      const updatedColors = [...colors, newColor.trim()];
      setColors(updatedColors);

      Object.assign(colorMap, updatedColorMap);

      form.setFieldsValue({ color: newColor.trim() });

      setNewColor("");
      setColorHex("#CCCCCC");
      setShowAddColor(false);
      message.success("Couleur ajoutée avec succès");
    } else {
      message.error("Veuillez saisir un nom de couleur valide");
    }
  };

  const handleFileChange = ({ fileList: newFileList }) => {
    setFileList(newFileList);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      const formData = new FormData();
      formData.append("matricule", values.matricule);
      formData.append("country", values.country);
      if (values.brand) formData.append("brand", values.brand);
      if (values.model) formData.append("model", values.model);
      if (values.color) formData.append("color", values.color);

      fileList.forEach((file) => {
        if (file.originFileObj) {
          formData.append("files[]", file.originFileObj);
        }
      });

      try {
        const response = await createVehicle(formData);
        message.success("Véhicule créé avec succès");

        if (onCreateVehicle) {
          onCreateVehicle(response);
        }
        setFileList([]);
        onCancel();
      } catch (error) {
        console.error("Erreur lors de la création du véhicule:", error);
        const errorMessage = error.response?.data?.error || "Erreur inconnue";
        getMessageError(errorMessage, "Erreur lors de la création du véhicule");
      } finally {
        setSubmitting(false);
      }
    } catch (errorInfo) {
      console.log("Validation échouée:", errorInfo);
    }
  };

  const brandDropdownRender = (menu) => (
    <>
      {menu}
      <Divider className="my-2" />
      {showAddBrand ? (
        <div className="px-3 pb-2">
          <Input
            placeholder="Saisir le nom de la marque"
            value={newBrand}
            onChange={(e) => setNewBrand(e.target.value)}
            className="mb-2"
          />
          <div className="flex justify-between">
            <Button size="small" onClick={() => setShowAddBrand(false)}>
              Annuler
            </Button>
            <Button
              type="primary"
              size="small"
              onClick={handleAddBrand}
              className="bg-indigo-600 hover:bg-indigo-700 border-0"
            >
              Ajouter
            </Button>
          </div>
        </div>
      ) : (
        <div className="px-3 pb-2">
          <Button
            type="link"
            block
            icon={<PlusOutlined />}
            onClick={() => setShowAddBrand(true)}
            className="text-center text-indigo-600"
          >
            Ajouter une marque
          </Button>
        </div>
      )}
    </>
  );

  const uploadProps = {
    name: "documents",
    multiple: true,
    fileList,
    beforeUpload: (file) => {
      const isValidType = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "image/jpeg",
        "image/png",
        "image/gif",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "text/plain",
      ].includes(file.type);

      const isLessThan10MB = file.size / 1024 / 1024 < 10;

      if (!isValidType) {
        message.error(`${file.name} n'est pas un type de fichier supporté`);
        return Upload.LIST_IGNORE;
      }

      if (!isLessThan10MB) {
        message.error(`${file.name} dépasse 10MB`);
        return Upload.LIST_IGNORE;
      }

      const processedFile = handleFilename(file);

      if (file.name !== processedFile.name) {
        message.info(
          `Fichier "${file.name}" renommé en "${processedFile.name}"`
        );
      }

      setFileList((prev) => [...prev, processedFile]);
      return false;
    },
    onRemove: (file) => {
      const index = fileList.indexOf(file);
      const newFileList = fileList.slice();
      newFileList.splice(index, 1);
      setFileList(newFileList);
    },
  };

  const colorDropdownRender = (menu) => (
    <>
      {menu}
      <Divider className="my-2" />
      {showAddColor ? (
        <div className="px-3 pb-2">
          <div className="flex mb-2">
            <Input
              placeholder="Nom de la couleur"
              value={newColor}
              onChange={(e) => setNewColor(e.target.value)}
              className="mr-2"
            />
            <Tooltip title="Choisir une couleur">
              <Input
                type="color"
                value={colorHex}
                onChange={(e) => setColorHex(e.target.value)}
                style={{ width: "50px", cursor: "pointer" }}
              />
            </Tooltip>
          </div>
          <div className="flex justify-between">
            <Button size="small" onClick={() => setShowAddColor(false)}>
              Annuler
            </Button>
            <Button
              type="primary"
              size="small"
              onClick={handleAddColor}
              className="bg-indigo-600 hover:bg-indigo-700 border-0"
            >
              Ajouter
            </Button>
          </div>
        </div>
      ) : (
        <div className="px-3 pb-2">
          <Button
            type="link"
            block
            icon={<PlusOutlined />}
            onClick={() => setShowAddColor(true)}
            className="text-center text-indigo-600"
          >
            Ajouter une couleur
          </Button>
        </div>
      )}
    </>
  );

  return (
    <Modal
      open={visible}
      title={
        <div className="flex items-center">
          <CarOutlined className="text-indigo-600 mr-2 text-xl" />
          <span>Nouveau Véhicule</span>
        </div>
      }
      onCancel={() => {
        setFileList([]);
        onCancel();
      }}
      footer={[
        <Button key="back" icon={<CloseOutlined />} onClick={onCancel}>
          Annuler
        </Button>,
        <Button
          key="submit"
          type="primary"
          icon={<SaveOutlined />}
          loading={submitting}
          onClick={handleSubmit}
          className="bg-indigo-600 hover:bg-indigo-700 border-0"
        >
          Créer
        </Button>,
      ]}
      width={600}
      destroyOnClose={true}
      className="vehicle-create-modal"
    >
      <Form form={form} layout="vertical" className="pt-4">
        <Form.Item
          name="matricule"
          label="Matricule"
          rules={[
            {
              required: true,
              message: "Veuillez saisir le matricule du véhicule",
            },
          ]}
        >
          <div>
            <Input
              placeholder="Saisir le matricule"
              className="rounded-md"
              autoComplete="off"
            />
            <div className="text-xs text-gray-500 mt-1">Exemple: 12345-A-1</div>
          </div>
        </Form.Item>

        <Form.Item
          name="country"
          label="Pays d'immatriculation"
          rules={[
            {
              required: true,
              message: "Veuillez sélectionner le pays d'immatriculation",
            },
          ]}
          initialValue="MA"
        >
          <Select
            showSearch
            placeholder="Sélectionnez un pays"
            optionFilterProp="children"
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            className="rounded-md"
          >
            {countries.map((country) => (
              <Option key={country.code} value={country.code}>
                {country.name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="brand" label="Marque">
          <Select
            showSearch
            placeholder="Sélectionnez une marque"
            optionFilterProp="children"
            className="rounded-md"
            allowClear
            dropdownRender={brandDropdownRender}
          >
            {brands.map((brand) => (
              <Option key={brand} value={brand}>
                {brand}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="model" label="Modèle">
          <Input
            placeholder="Saisir le modèle"
            className="rounded-md"
            autoComplete="off"
          />
        </Form.Item>

        <Form.Item name="color" label="Couleur">
          <Select
            showSearch
            placeholder="Sélectionnez une couleur"
            optionFilterProp="children"
            className="rounded-md"
            allowClear
            dropdownRender={colorDropdownRender}
          >
            {colors.map((color) => (
              <Option key={color} value={color}>
                <div className="flex items-center">
                  <div
                    className="w-4 h-4 rounded-full mr-2"
                    style={{ backgroundColor: colorMap[color] || "#CCCCCC" }}
                  />
                  {color}
                </div>
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="files" label="Documents">
          <Upload
            fileList={fileList}
            onChange={handleFileChange}
            multiple={true}
            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.txt"
            {...uploadProps}
          >
            <Button icon={<UploadOutlined />}>Sélectionner des fichiers</Button>
          </Upload>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default VehicleCreateModal;
